interface Pin {
  value: number;
  label: string;
  sublabel: string;
}

interface MetricRangeVisualProps {
  min?: number;
  max?: number;
  pins: Pin[];
  lowLabel: string;
  highLabel: string;
  color?: string;
}

export function MetricRangeVisual({
  min = 0,
  max = 1,
  pins,
  lowLabel,
  highLabel,
  color = '#0a104f',
}: MetricRangeVisualProps) {
  const range = max - min;

  return (
    <div className="my-4">
      {/* Range bar + pin markers */}
      <div className="relative px-2">
        {/* Track */}
        <div className="h-2 rounded-full bg-gray-200 relative">
          <div
            className="absolute left-0 top-0 h-full rounded-full opacity-30"
            style={{ width: '100%', backgroundColor: color }}
          />
          {/* Gradient fill */}
          <div
            className="absolute left-0 top-0 h-full rounded-full"
            style={{
              background: `linear-gradient(to right, ${color}33, ${color})`,
              width: '100%',
            }}
          />
          {/* Pin markers */}
          {pins.map((pin, idx) => {
            const pct = ((pin.value - min) / range) * 100;
            return (
              <div
                key={idx}
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
                style={{ left: `${pct}%` }}
              >
                <div
                  className="w-3 h-3 rounded-full border-2 border-white shadow"
                  style={{ backgroundColor: color }}
                />
              </div>
            );
          })}
        </div>

        {/* Range labels */}
        <div className="flex justify-between mt-1">
          <span className="text-[10px] text-gray-400">{min.toFixed(2)}</span>
          <span className="text-[10px] text-gray-400">{max.toFixed(2)}</span>
        </div>
      </div>

      {/* Pin detail cards */}
      <div className="mt-3 flex flex-wrap gap-3">
        {pins.map((pin, idx) => {
          const pct = ((pin.value - min) / range) * 100;
          return (
            <div
              key={idx}
              className="flex-1 min-w-[120px] rounded-md border px-3 py-2"
              style={{ borderColor: `${color}33`, backgroundColor: `${color}08` }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold" style={{ color }}>
                  {pin.label}
                </span>
                <span
                  className="text-xs font-bold tabular-nums px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: color, color: '#fff' }}
                >
                  {pin.value.toFixed(2)}
                </span>
              </div>
              <p className="text-[10px] text-gray-500 leading-snug">{pin.sublabel}</p>
              {/* Mini bar */}
              <div className="mt-1.5 h-1 rounded-full bg-gray-200 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Low/High labels */}
      <div className="flex justify-between mt-2 px-1">
        <span className="text-[10px] text-gray-400 italic">{lowLabel}</span>
        <span className="text-[10px] text-gray-400 italic">{highLabel}</span>
      </div>
    </div>
  );
}
